/**
 * Roof Tool - Roof by Footprint or Extrusion
 * 
 * Roofs are created by sketching a footprint with slope definitions.
 * Features:
 * - Footprint sketch with slope arrows
 * - Overhang settings
 * - Multiple roof types
 */

import { v4 as uuidv4 } from 'uuid';
import { RoofElement, RoofGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface RoofToolOptions {
    roofType: string;
    slope: number; // degrees
    overhang: number; // mm
    levelId: string;
    offset: number; // mm from level
    cutoff: 'None' | string; // Cutoff level or None
    definesSlope: boolean[]; // Array matching footprint segments
}

export class RoofTool extends BaseTool {
    id = 'roof';
    name = 'Roof';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.points.push(snapped);

        // Check if closed
        if (this.points.length > 2) {
            const start = this.points[0];
            if (snapped.distanceTo(start) < 0.2) {
                this.points.pop();
                this.finishSketch();
                return;
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 3) {
                this.finishSketch();
            }
        } else if (event.key === 'Escape') {
            if (this.points.length > 0) {
                this.points = [];
                this.updatePreview();
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.points.length > 0 && this.currentPoint) {
            const previewPoints = [...this.points, this.currentPoint];
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: previewPoints,
                closed: false,
                color: '#ff0000' // Red for roof
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private finishSketch() {
        if (!this.context) return;

        const boundary: [number, number][] = this.points.map(p => [p.x, p.y]);


        const options: RoofToolOptions = {
            roofType: 'Basic Roof: Generic - 300mm',
            slope: 30,
            overhang: 0,
            levelId: 'Level 2', // Should get top level
            offset: 0,
            cutoff: 'None',
            definesSlope: boundary.map(() => true) // Default Hip Roof
        };

        const roof = createRoof(boundary, options);
        this.context.bimStore.addElement(roof);

        this.points = [];
        this.context.appStore.setToolPreview(null);
    }
}

export function createRoof(
    footprint: [number, number][],
    options: RoofToolOptions
): RoofElement {
    const overhangM = options.overhang / 1000;

    // Determine roof shape based on definesSlope
    let derivedType: 'Gable' | 'Hip' | 'Flat' | 'Shed' = 'Flat';
    const slopeCount = options.definesSlope.filter(Boolean).length;

    if (slopeCount === 0) derivedType = 'Flat';
    else if (slopeCount === 1) derivedType = 'Shed';
    else if (slopeCount === footprint.length) derivedType = 'Hip';
    else derivedType = 'Gable';

    const geometryData: RoofGeometryData = {
        footprint: footprint,
        slope: options.slope,
        overhang: overhangM,
        baseLevelId: options.levelId,
        baseOffsetFromLevel: options.offset / 1000,
        roofType: derivedType,
        definesSlope: options.definesSlope
    };

    return {
        id: uuidv4(),
        category: 'Roofs',
        type: options.roofType,
        parameters: {
            'Slope': `${options.slope}°`,
            'Overhang': `${overhangM.toFixed(2)} m`,
            'Base Level': options.levelId,
            'Base Offset From Level': `${options.offset} mm`,
            'Cutoff Level': options.cutoff,
            'Area': calculatePolygonArea(footprint).toFixed(2) + ' m²',
            'Rafter or Truss': 'Rafter'
        },
        constraints: [],
        geometryData
    };
}

function calculatePolygonArea(points: [number, number][]): number {
    let area = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += points[i][0] * points[j][1];
        area -= points[j][0] * points[i][1];
    }
    return Math.abs(area) / 2;
}

export const DEFAULT_ROOF_TYPES = [
    'Basic Roof: Generic - 300mm',
    'Basic Roof: Steel Truss - Insulation on Metal Deck',
    'Basic Roof: Wood Rafter 200mm - Asphalt Shingle',
    'Basic Roof: SIP 200mm Panel'
];
